import type { LucideIcon } from "lucide-react";
import type { BaseCard } from "modules/cards/model/card";
import { ReplacedSymbolsOracleText } from "modules/common/components/ManaFontSymbols";
import { cn } from "utils/presentation";

export type DossierBadgeTone =
  | "base"
  | "primary"
  | "secondary"
  | "accent"
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "error";

export interface DossierBadgeGroup {
  label: string;
  values: readonly string[];
  tone?: DossierBadgeTone;
  toneByValue?: Readonly<Record<string, DossierBadgeTone>>;
}

export type DossierStatTone =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "neutral";

export interface DossierStat {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  tone?: DossierStatTone;
}

export interface DossierCallout {
  label: string;
  text: string;
}

interface GameCardDossierProps {
  card: BaseCard;
  badges?: readonly DossierBadgeGroup[];
  stats?: readonly DossierStat[];
  callouts?: readonly DossierCallout[];
  useManaFontSymbols?: boolean;
  costTone?: DossierStatTone;
}

const BADGE_TONE_CLASSES: Record<DossierBadgeTone, string> = {
  base: "border-base-content/15 bg-base-content/10 text-base-content",
  primary: "badge-primary",
  secondary: "badge-secondary",
  accent: "badge-accent",
  neutral: "border-base-content/15 bg-base-content/10 text-base-content",
  info: "badge-info",
  success: "badge-success",
  warning: "badge-warning",
  error: "badge-error",
};

const STAT_TONE_CLASSES: Record<DossierStatTone, string> = {
  info: "bg-info text-info-content",
  success: "bg-success text-success-content",
  warning: "bg-warning text-warning-content",
  error: "bg-error text-error-content",
  neutral: "bg-neutral text-neutral-content",
};

export function GameCardDossier({
  card,
  badges = [],
  stats = [],
  callouts = [],
  useManaFontSymbols = false,
  costTone,
}: GameCardDossierProps) {
  const visibleBadges = badges.filter((group) => group.values.length > 0);
  const visibleStats = stats.filter(
    (stat) =>
      stat.value !== "" && stat.value !== null && stat.value !== undefined
  );

  return (
    <div className="flex flex-col gap-3">
      <header className="mt-2 md:mt-0">
        <div className="flex items-start justify-between gap-3">
          <h1 id="card-title" className="text-primary text-xl font-semibold">
            {card.name}
          </h1>
          {card.costPrinted && (
            <span
              className={cn(
                "badge shrink-0 gap-1.5 font-semibold",
                costTone ? STAT_TONE_CLASSES[costTone] : "badge-soft"
              )}
              aria-label={`Cost ${card.costPrinted}`}
            >
              <span
                className={cn(
                  "text-[0.5625rem] font-medium",
                  costTone ? "opacity-70" : "text-base-content/50"
                )}
              >
                Cost
              </span>
              {card.costPrinted}
            </span>
          )}
        </div>
        {card.type && (
          <p className="text-secondary mt-1 text-sm">{card.type}</p>
        )}
      </header>

      {visibleBadges.map((group) => (
        <section
          key={group.label}
          aria-label={group.label}
          className="flex flex-wrap items-center gap-1.5"
        >
          <span className="text-base-content/45 mr-0.5 text-[0.5625rem] font-medium">
            {group.label}
          </span>
          {group.values.map((value) => {
            const valueTone = group.toneByValue?.[value.trim().toLowerCase()];

            return (
              <span
                key={value}
                className={cn(
                  "badge badge-sm badge-soft",
                  BADGE_TONE_CLASSES[valueTone ?? group.tone ?? "neutral"]
                )}
              >
                {value}
              </span>
            );
          })}
        </section>
      ))}

      {visibleStats.length > 0 && (
        <dl className="stats stats-horizontal border-base-300 bg-base-200/40 w-full overflow-x-auto border shadow-none">
          {visibleStats.map(({ label, value, icon: Icon, tone }) => (
            <div
              key={label}
              className={cn(
                "stat min-w-24 px-3 py-2.5",
                tone && STAT_TONE_CLASSES[tone]
              )}
            >
              <dt
                className={cn(
                  "stat-title flex items-center gap-1 text-xs",
                  tone && "text-inherit opacity-80"
                )}
              >
                {Icon && <Icon aria-hidden size={13} />}
                {label}
              </dt>
              <dd className="stat-value text-base font-bold">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {callouts.map((callout) => (
        <section
          key={callout.label}
          aria-label={callout.label}
          className="border-base-300 bg-base-200/40 rounded-box border px-3 py-2.5"
        >
          <h2 className="text-secondary text-[0.6875rem] font-medium">
            {callout.label}
          </h2>
          <ReplacedSymbolsOracleText
            oracleText={callout.text}
            className="mt-1 text-sm"
            replaceSymbols={useManaFontSymbols}
          />
        </section>
      ))}

      {card.text && (
        <section
          aria-label="Card text"
          className="border-base-300 border-t pt-3"
        >
          <ReplacedSymbolsOracleText
            oracleText={card.text}
            className="text-sm"
            replaceSymbols={useManaFontSymbols}
          />
        </section>
      )}

      {card.flavorText && (
        <ReplacedSymbolsOracleText
          oracleText={card.flavorText}
          className="text-secondary text-sm italic"
          replaceSymbols={useManaFontSymbols}
        />
      )}

      {(card.artist || card.number) && (
        <footer className="text-secondary flex flex-wrap gap-x-3 text-xs">
          {card.artist && <span>Illus. {card.artist}</span>}
          {card.number && <span>#{card.number}</span>}
        </footer>
      )}
    </div>
  );
}
